import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Camera, Calculator, TrendingUp, TrendingDown, AlertTriangle, X, Carrot, ChefHat, ReceiptText } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, gbp } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function Dashboard() {
  const [data, setData] = useState(null);
  const { user } = useAuth();

  const load = useCallback(() => api.get("/dashboard").then((r) => setData(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const dismiss = async (id) => {
    try {
      await api.post(`/alerts/${id}/dismiss`);
      toast.success("Alert dismissed");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  if (!data) return <p className="text-ink/50 py-10" data-testid="dashboard-loading">Loading…</p>;

  const stats = [
    { label: "Ingredients", value: data.ingredient_count, icon: Carrot, to: "/ingredients" },
    { label: "Recipes", value: data.recipe_count, icon: ChefHat, to: "/recipes" },
    { label: "Receipts", value: data.purchase_count, icon: ReceiptText, to: "/capture" },
  ];

  return (
    <div className="space-y-8 animate-rise" data-testid="dashboard-page">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] font-medium text-ink/50">Good to see you</p>
        <h1 className="font-heading font-bold text-4xl sm:text-5xl tracking-tight mt-1">{user?.name?.split(" ")[0]}'s kitchen</h1>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Link to="/capture" data-testid="dashboard-capture-cta"
          className="group bg-tea text-tea-text rounded-lg p-6 flex items-center gap-4 transition-transform duration-200 hover:-translate-y-1 hover:shadow-md">
          <Camera size={28} />
          <div>
            <p className="font-heading font-bold text-lg">Snap a receipt</p>
            <p className="text-white/80 text-sm">Photo in, prices out. No typing.</p>
          </div>
        </Link>
        <Link to="/quote" data-testid="dashboard-quote-cta"
          className="group bg-almond text-almond-text rounded-lg p-6 flex items-center gap-4 transition-transform duration-200 hover:-translate-y-1 hover:shadow-md">
          <Calculator size={28} />
          <div>
            <p className="font-heading font-bold text-lg">Price a quote</p>
            <p className="text-white/80 text-sm">Your minimum price, before you send.</p>
          </div>
        </Link>
      </div>

      {data.alerts.length > 0 && (
        <section data-testid="price-drift-alerts">
          <h2 className="text-xs uppercase tracking-[0.2em] font-medium text-ink/50 mb-3">Alerts</h2>
          <div className="space-y-2">
            {data.alerts.map((a) => {
              const type = a.type || "price_drift";
              if (type === "low_stock") {
                return (
                  <div key={a.id} data-testid={`low-stock-alert-${a.id}`}
                    className="flex items-center justify-between gap-3 rounded-lg border p-4 bg-warnamber/10 border-warnamber/30">
                    <div className="flex items-center gap-3">
                      <AlertTriangle className="text-warnamber shrink-0" size={20} />
                      <p className="text-sm">
                        <span className="font-medium">{a.ingredient_name}</span> is low —{" "}
                        <span className="font-mono-num font-semibold">{a.current_stock}{a.unit}</span> left
                        <span className="text-ink/50"> · reorder at {a.reorder_threshold}{a.unit}</span>
                      </p>
                    </div>
                    <button onClick={() => dismiss(a.id)} data-testid={`dismiss-alert-${a.id}`} className="p-1 rounded hover:bg-sand text-ink/40"><X size={16} /></button>
                  </div>
                );
              }
              if (type === "cheaper_product_available") {
                return (
                  <div key={a.id} data-testid={`cheaper-product-alert-${a.id}`}
                    className="flex items-center justify-between gap-3 rounded-lg border p-4 bg-moss/10 border-moss/30">
                    <div className="flex items-center gap-3">
                      <TrendingDown className="text-moss shrink-0" size={20} />
                      <p className="text-sm">
                        <span className="font-medium">{a.alt_product_name}</span> is{" "}
                        <span className="font-mono-num font-semibold">{a.pct_change}%</span> cheaper than {a.product_name} for {a.ingredient_name}
                        <span className="text-ink/50"> &middot; {gbp(a.new_price)} vs {gbp(a.old_price)}/{a.unit}</span>
                      </p>
                    </div>
                    <button onClick={() => dismiss(a.id)} data-testid={`dismiss-alert-${a.id}`} className="p-1 rounded hover:bg-sand text-ink/40"><X size={16} /></button>
                  </div>
                );
              }
              return (
                <div key={a.id} data-testid={`drift-alert-${a.id}`}
                  className={`flex items-center justify-between gap-3 rounded-lg border p-4 ${a.pct_change > 0 ? "bg-terracotta/10 border-terracotta/30" : "bg-moss/10 border-moss/30"}`}>
                  <div className="flex items-center gap-3">
                    {a.pct_change > 0 ? <TrendingUp className="text-terracotta shrink-0" size={20} /> : <TrendingDown className="text-moss shrink-0" size={20} />}
                    <p className="text-sm">
                      <span className="font-medium">{a.ingredient_name}</span>{" "}
                      {a.pct_change > 0 ? "up" : "down"} <span className="font-mono-num font-semibold">{Math.abs(a.pct_change)}%</span> since last purchase
                      <span className="text-ink/50"> · {gbp(a.old_price)} → {gbp(a.new_price)}/{a.unit}</span>
                    </p>
                  </div>
                  <button onClick={() => dismiss(a.id)} data-testid={`dismiss-alert-${a.id}`} className="p-1 rounded hover:bg-sand text-ink/40"><X size={16} /></button>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <div className="grid grid-cols-3 gap-4">
        {stats.map(({ label, value, icon: Icon, to }) => (
          <Link key={label} to={to} data-testid={`stat-${label.toLowerCase()}`}
            className="bg-surface border border-black/5 rounded-lg p-5 transition-transform duration-200 hover:-translate-y-1 hover:shadow-md">
            <Icon size={18} className="text-clay" />
            <p className="font-mono-num text-2xl font-semibold mt-2">{value}</p>
            <p className="text-xs text-ink/50 mt-1">{label}</p>
          </Link>
        ))}
      </div>

      <section>
        <h2 className="text-xs uppercase tracking-[0.2em] font-medium text-ink/50 mb-3">Recent purchases</h2>
        {data.recent_purchases.length === 0 ? (
          <div className="bg-surface border border-dashed border-sand rounded-lg p-8 text-center" data-testid="no-purchases-empty-state">
            <p className="text-ink/60 text-sm">No receipts yet. Snap your first one and the confirm screen does the rest.</p>
          </div>
        ) : (
          <div className="divide-y divide-sand" data-testid="recent-purchases-list">
            {data.recent_purchases.map((p) => (
              <div key={p.id} className="flex items-center justify-between py-3">
                <div>
                  <p className="font-medium text-sm">{p.supplier_name}</p>
                  <p className="text-xs text-ink/50">{p.purchased_at} · {p.lines?.length || 0} items</p>
                </div>
                <p className="font-mono-num font-semibold">{gbp(p.total_amount)}</p>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
